import React, { useEffect, useMemo } from "react";
import _ from 'lodash';
import cx from 'classnames';
import dateformat from "dateformat";

import styles from './entity.module.scss';
import { fetchStreamChildren, fetchStreams } from "src/redux/actions/streams";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import Reveal from "../animation";
import { fetchCollection, fetchCollections, fetchCollectionSection } from "src/redux/actions/collections";
import InfiniteScroll from "../infiniteScroll";
import { USER_DEFINED } from "src/constants/collections";
import { useSpring, animated } from "@react-spring/web";
import { Stream } from "src/types/streams";
import { Collection, CollectionSection } from "src/types/collections";
import Thumbnail from "../thumbnail";
import { FileService } from "src/utils/file.service";
import useMeasure from "react-use-measure";
import { fetchGroupChildren } from "src/redux/actions/groups";
import Text from "../text";
import userRoleService from "src/utils/userRoles.service";

interface EntityBrowserProps {
  className?: string;
  width: number;
  entity?: Stream | Collection | CollectionSection;
  items?: any[];
  // eslint-disable-next-line no-unused-vars
  action?: (entity: any) => React.ReactNode;
  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
}

const EntityBrowser: React.FC<EntityBrowserProps> = ({ className, width, items, action, onSelection }) => {
  return (
    <div className={cx(styles.container, className)}>
      {items && items.map((item) => (
        <EntityRow key={_.get(item, "id")} entity={item} width={width} action={action} onSelection={onSelection} />
      ))}
      {items?.length === 0 && (
        <div className={styles.empty}>
          No items found
        </div>
      )}
    </div>
  );
}


const StreamsBrowser: React.FC<EntityBrowserProps> = ({ className, width, action, onSelection }) => {
  const dispatch = useDispatch();
  const { loading, streams } = useSelector((state: any) => {
    return {
      loading: state.hub.streams.loading,
      streams: state.hub.streams?.items,
    };
  }, _.isEqual);

  useEffect(() => {
    dispatch(fetchStreams());
  }, []);

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <Reveal show={loading} className={styles.stack}>
        <StreamPlaceholder width={'50%'} />
        <StreamPlaceholder width={'70%'} />
        <StreamPlaceholder width={'60%'} />
        <StreamPlaceholder width={'50%'} />
      </Reveal>
      <Reveal show={!loading} className={styles.stack}>
        <EntityBrowser className={className} items={streams} width={width} action={action} onSelection={onSelection} />
      </Reveal>
    </div>
  );
}

const StreamBrowser: React.FC<EntityBrowserProps> = ({ className, width, entity, action, onSelection }) => {
  const dispatch = useDispatch();
  const { loading, items } = useSelector((state: any) => {
    if (!entity) {
      return {
        loading: false,
        items: []
      }
    }

    return {
      loading: state.stream.items[entity!.id!]?.loadingChildren,
      items: state.stream.items[entity!.id!]?.item?.children,
    };
  }, _.isEqual);

  useEffect(() => {
    if (entity?.id) {
      dispatch(fetchStreamChildren({
        id: entity.id,
        page: 0,
        limit: 200,
        sort: {
          type: 'name',
          order: 'asc'
        }
      }));
    }
  }, [entity?.id]);

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <InfiniteScroll className={styles.scroll}
        loading={loading}
        hasMore={false}
        loadMore={() => { }}
        loader={() => {
          return (
            <>
              {_.times(_.size(items) === 0 ? 3 : 1, (index) => (
                <AssetPlaceholder key={index} />
              ))}
            </>
          )
        }}
      >
        <EntityBrowser className={className} width={width} items={items} action={action} onSelection={onSelection} />
      </InfiniteScroll>
    </div>
  );
}

const GroupBrowser: React.FC<EntityBrowserProps> = ({ className, width, entity, action, onSelection }) => {
  const dispatch = useDispatch();
  const { loading, items } = useSelector((state: any) => {
    if (!entity) {
      return {
        loading: false,
        items: []
      }
    }

    return {
      loading: state.group[entity!.id!]?.loadingChildren,
      items: state.group[entity!.id!]?.item?.children,
    };
  }, _.isEqual);

  useEffect(() => {
    if (entity?.id) {
      dispatch(fetchGroupChildren({
        id: entity.id,
        page: 0,
        limit: 200,
        sort: {
          type: 'name',
          order: 'asc'
        }
      }));
    }
  }, [entity?.id]);

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <InfiniteScroll className={styles.scroll}
        loading={loading}
        hasMore={false}
        loadMore={() => { }}
        loader={() => {
          return (
            <>
              {_.times(_.size(items) === 0 ? 3 : 1, (index) => (
                <AssetPlaceholder key={index} />
              ))}
            </>
          )
        }}
      >
        <EntityBrowser className={className} width={width} items={items} action={action} onSelection={onSelection} />
      </InfiniteScroll>
    </div>
  );
}

const CollectionsBrowser: React.FC<EntityBrowserProps> = ({ className, width, action, onSelection }) => {
  const dispatch = useDispatch();

  const { loading, collections, nextPage, hasMoreItems } = useSelector((state: any) => {
    const collectionState = _.get(state, 'collections');
    const loading = _.get(collectionState, 'loading');
    const pagination = _.get(collectionState, 'pagination');
    const total = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");

    const collections = (_.get(collectionState, 'items') || []).filter((item: any) => {
      return item.type === USER_DEFINED;
    });

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      loading: loading,
      collections: collections,
      nextPage: nextPage,
      hasMoreItems: hasMoreItems
    };
  }, _.isEqual);

  useEffect(() => {
    if(userRoleService.canAccessCollection()){
      fetchCollectionsForPage(0);
    }
  }, []);

  const fetchCollectionsForPage = (page: number) => {

    const request = {
      page: page,
      limit: 20,
      sort: {
        field: 'name',
        order: 'asc'
      }
    };

    dispatch(fetchCollections(request));
  }

  const handleLoadMoreCollections = () => {
    if (nextPage) {
      fetchCollectionsForPage(nextPage);
    }
  }

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <InfiniteScroll className={styles.scroll}
        loading={loading}
        hasMore={hasMoreItems}
        loadMore={handleLoadMoreCollections}
        loader={() => {
          if (_.size(collections) === 0) {
            return (<>
              <CollectionPlaceholder width={'50%'} />
              <CollectionPlaceholder width={'70%'} />
              <CollectionPlaceholder width={'60%'} />
              <CollectionPlaceholder width={'50%'} />
            </>)
          } else {
            return <CollectionPlaceholder width={'70%'} />
          }
        }}
      >
        <EntityBrowser className={className}
          width={width}
          items={collections}
          action={action}
          onSelection={onSelection}
        />
      </InfiniteScroll>
    </div>
  );
}

const CollectionBrowser: React.FC<EntityBrowserProps> = ({ className, width, entity, action, onSelection }) => {
  const dispatch = useDispatch();
  const collection = entity as Collection;
  const childrenType = collection.sectionsCount! > 0 ? 'collection_section' : 'asset';

  const { loading, items, loaded } = useSelector((state: any) => {
    if (!entity) {
      return {
        loading: false,
        items: [],
        loaded: false,
      }
    }

    const entry = state.collections.collection[entity!.id!];
    const collection = entry?.item;
    let items = [];
    if (collection) {
      if (_.size(collection.sections) > 0) {
        items = (collection.sections || []).map((item: any) => {
          return {
            ...item.item,
            entityType: 'collection_section'
          }
        });
      } else if (_.size(collection.entities) > 0) {
        items = (collection.entities || []).map((item: any) => {
          return {
            ...item.asset
          }
        });
      }
    }

    return {
      loading: entry?.loading,
      items: items,
      loaded: entry?.loaded ?? false,
    };
  }, _.isEqual);

  useEffect(() => {
    if (entity?.id && !loaded) {
      dispatch(fetchCollection(entity.id));
    }
  }, [entity?.id, loaded, dispatch]);

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <Reveal show={loading} className={styles.stack}>
        {childrenType === 'collection_section' && (
          <>
            <CollectionSectionPlaceholder width="50%" />
            <CollectionSectionPlaceholder width="70%" />
            <CollectionSectionPlaceholder width="60%" />
            <CollectionSectionPlaceholder width="50%" />
          </>
        )}

        {childrenType === 'asset' && (
          <>
            {_.times((collection.assetsCount || 3), (index) => (
              <AssetPlaceholder key={index} />
            ))}
          </>
        )}

      </Reveal>
      <Reveal show={!loading} className={styles.stack}>
        <EntityBrowser className={className} items={items} width={width} action={action} onSelection={onSelection} />
      </Reveal>
    </div>
  );
}

const CollectionSectionBrowser: React.FC<EntityBrowserProps> = ({ className, width, entity, action, onSelection }) => {
  const dispatch = useDispatch();
  const collectionSection = entity as CollectionSection;

  const { loading, items } = useSelector((state: any) => {
    if (!entity) {
      return {
        loading: false,
        items: []
      }
    }

    const collectionState = state.collections.collection[collectionSection!.collection!]?.item;
    if (!collectionState) {
      return {
        loading: false,
        items: []
      }
    }

    let items = [];
    let loading = false;
    const sectionState = collectionState.sections.find(section => section.item?.id === collectionSection.id);

    if (sectionState) {
      items = (sectionState.item.entities || []).map((item: any) => item.asset);
      loading = sectionState.loading;
    }

    return {
      loading: loading,
      items: items,
    };
  }, _.isEqual);

  useEffect(() => {
    if (entity?.id) {
      dispatch(fetchCollectionSection({
        collection: collectionSection!.collection!,
        section: entity.id
      }));
    }
  }, [entity?.id]);

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <Reveal show={loading} className={styles.stack}>
        <>
          {_.times(3, (index) => (
            <AssetPlaceholder key={index} />
          ))}
        </>
      </Reveal>
      <Reveal show={!loading} className={styles.stack}>
        <EntityBrowser className={className} items={items} width={width} action={action} onSelection={onSelection} />
      </Reveal>
    </div>
  );
}

/**
 * Entity Renders
*/

interface EntityRowProps {
  entity: any;
  width: number;
  // eslint-disable-next-line no-unused-vars
  action?: (entity: any) => React.ReactNode;
  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
}

const EntityRow: React.FC<EntityRowProps> = ({ entity, width, action, onSelection }) => {
  const Component = useMemo(() => {
    const type = entity?.entityType;
    switch (type) {
      case 'stream':
        return StreamRow;
      case 'group':
        return GroupRow;
      case 'collection':
        return CollectionRow;
      case 'collection_section':
        return CollectionSectionRow;
      case 'asset':
        return AssetRow;
      default:
        return false;
    }
  }, [entity]);


  if (!Component) {
    return null;
  }

  return (
    React.createElement(Component, { entity, width, action, onSelection })
  );
}

const StreamRow: React.FC<EntityRowProps> = ({ entity, width, action, onSelection }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [mountChildren, setMountChildren] = React.useState(false);
  const [showTooltip, setShowTooltip] = React.useState(false);
  const [containerRef, containerBounds] = useMeasure();

  const [childrenAnimation] = useSpring({
    opacity: expanded ? 1 : 0,
    height: expanded ? containerBounds.height : 0,
    from: { opacity: 0, height: 0 },
  }, [expanded, containerBounds]);

  const handleClick = () => {
    setExpanded(!expanded);
    setMountChildren(true);
  }

  return (
    <>
      <div className={cx(styles.entity, styles.expandable, { [styles.expanded]: expanded })} style={{ width: width }} onClick={handleClick}>
        <Text text={entity?.name || ""} lines={1} className={styles.name} tooltip={{placement: "auto"}} />
        <div className={styles.actions}>
          <button className={styles.action}>
            <i className={cx(styles.arrow, "paperflite-dropdown")}></i>
          </button>
        </div>
      </div>
      {mountChildren && (
        <animated.div style={childrenAnimation} className={styles.children}>
          <div ref={containerRef} className={styles.inner}>
            <StreamBrowser entity={entity} width={width - 10} action={action} onSelection={onSelection} />
          </div>
        </animated.div>
      )}
    </>
  );
}

const GroupRow: React.FC<EntityRowProps> = ({ entity, width, action, onSelection }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [mountChildren, setMountChildren] = React.useState(false);
  const [showTooltip, setShowTooltip] = React.useState(false);
  const [containerRef, containerBounds] = useMeasure();

  const [childrenAnimation] = useSpring({
    opacity: expanded ? 1 : 0,
    height: expanded ? containerBounds.height : 0,
    from: { opacity: 0, height: 0 },
  }, [expanded, containerBounds]);

  const handleClick = () => {
    setExpanded(!expanded);
    setMountChildren(true);
  }

  return (
    <>
      <div className={cx(styles.entity, styles.expandable, { [styles.expanded]: expanded })} style={{ width: width }} onClick={handleClick}>
        <div className={styles.name}> 
          <Text text={entity?.name || ""} tooltip={{placement: "auto"}} />
        </div>
        <div className={styles.actions}>
          <button className={styles.action}>
            <i className={cx(styles.arrow, "paperflite-dropdown")}></i>
          </button>
        </div>
      </div>
      <animated.div style={childrenAnimation}>
        {mountChildren && (
          <animated.div style={childrenAnimation} className={styles.children}>
            <div ref={containerRef} className={styles.inner}>
              <GroupBrowser entity={entity} width={width - 15} action={action} onSelection={onSelection} />
            </div>
          </animated.div>
        )}
      </animated.div>
    </>
  );
}

const CollectionRow: React.FC<EntityRowProps> = ({ entity, width, action, onSelection }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [mountChildren, setMountChildren] = React.useState(false);
  const [showTooltip, setShowTooltip] = React.useState(false);
  const [containerRef, containerBounds] = useMeasure();

  const actionComponent = useMemo(() => { 
    return action && action(entity);
  }, [action]);

  const [childrenAnimation] = useSpring({
    opacity: expanded ? 1 : 0,
    height: expanded ? containerBounds.height : 0,
    from: { opacity: 0, height: 0 },
  }, [expanded, containerBounds]);

  const handleClick = () => {
    setExpanded(!expanded);
    setMountChildren(true);
    if (!action) {
      onSelection(entity);
    }
  }

  const handleActionClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    onSelection(entity);
  }

  return (
    <>
      <div className={cx(styles.entity, styles.expandable, styles.collection, { [styles.expanded]: expanded })} style={{ width: width }} onClick={handleClick}>
        <div className={styles.inner}>
          <Text  text={entity?.name || ""} lines={1} className={styles.name} tooltip={{placement: "auto"}} />
          <button className={styles.action}>
            <i className={cx(styles.arrow, "paperflite-dropdown")}></i>
          </button>
        </div>
        <div className={cx(styles.actions, {
          [styles.hasActions]: actionComponent
        })}>
          <div className={styles.action} onClick={handleActionClick}>
            {actionComponent}
          </div>

          <span className={styles.count}>
            {entity?.sectionsCount > 0 ? `${entity?.sectionsCount} Sections` : ''}
            {entity?.sectionsCount === 0 ? `${entity?.assetsCount} Assets` : ''}
          </span>

        </div>
      </div>
      {mountChildren && (
        <animated.div style={childrenAnimation} className={styles.children}>
          <div ref={containerRef} className={styles.inner}>
            <CollectionBrowser entity={entity} width={width - 10} action={action} onSelection={onSelection} />
          </div>
        </animated.div>
      )}
    </>
  );
}

const CollectionSectionRow: React.FC<EntityRowProps> = ({ entity, width, action, onSelection }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [showTooltip, setShowTooltip] = React.useState(false);
  const [mountChildren, setMountChildren] = React.useState(false);
  const [containerRef, containerBounds] = useMeasure();

  const [childrenAnimation] = useSpring({
    opacity: expanded ? 1 : 0,
    height: expanded ? containerBounds.height : 0,
    from: { opacity: 0, height: 0 },
  }, [expanded, containerBounds]);

  const handleClick = () => {
    setExpanded(!expanded);
    setMountChildren(true);
  }

  return (
    <>
      <div className={cx(styles.entity, styles.expandable, styles.collection)} style={{ width: width }} onClick={handleClick}>
        <Text className={styles.name} text={entity?.name || ""} lines={1} tooltip={{placement: "auto"}} />
        <div className={styles.actions}>
          <button className={styles.action}>
            <i className={cx(styles.arrow, "paperflite-dropdown")}></i>
          </button>
        </div>
      </div>
      {mountChildren && (
        <animated.div style={childrenAnimation} className={styles.children}>
          <div ref={containerRef} className={styles.inner}>
            <CollectionSectionBrowser entity={entity} width={width - 15} action={action} onSelection={onSelection} />
          </div>
        </animated.div>
      )}
    </>
  );
}

const AssetRow: React.FC<EntityRowProps> = ({ entity, width, action, onSelection }) => {
  const [showTooltip, setShowTooltip] = React.useState(false);
  const fileType = useMemo(() => {
    return FileService.getFileType(entity.metadata);
  }, [entity]);

  const updatedDate = _.get(entity, 'contentModifiedDate');

  const handleClick = () => {
    if (onSelection) {
      onSelection(entity);
    }
  }

  return (
    <div className={cx(styles.entity, styles.asset)} style={{ width: width }} onClick={handleClick}>
      <div className={styles.inner}>
        <Thumbnail className={styles.icon} media={entity?.icon} width={68} height={80} defaultImage="/images/assets/default_asset.jpg" />
        <div className={styles.details}>
          <Text text={entity?.name || ""} lines={1} className={styles.name} tooltip={{placement: "auto"}} />
          <div className={styles.meta}>
            {fileType} {updatedDate && `| Updated ${dateformat(updatedDate, 'dd mmm yyyy')}`}
          </div>
        </div>
      </div>
      <div className={styles.actions}>
        {action &&
          <div className={styles.action}>{action(entity)}</div>
        }
      </div>
    </div>
  );
}

export const StreamPlaceholder: React.FC<{ width: string }> = ({ width }) => {
  return (
    <div className={cx(styles.entity, styles.placeholder)} style={{ width: width }}>
      <div className={cx(styles.name, styles.block)}></div>
    </div>
  );
}

export const CollectionPlaceholder: React.FC<{ width: string }> = ({ width }) => {
  return (
    <div className={cx(styles.entity, styles.placeholder)} style={{ width: width }}>
      <div className={cx(styles.name, styles.block)}></div>
    </div>
  );
}

export const CollectionSectionPlaceholder: React.FC<{ width: string }> = ({ width }) => {
  return (
    <div className={cx(styles.entity, styles.placeholder)} style={{ width: width }}>
      <div className={cx(styles.name, styles.block)}></div>
    </div>
  );
}

export const AssetPlaceholder: React.FC<any> = () => {
  return (
    <div className={cx(styles.entity, styles.asset, styles.placeholder)}>
      <div className={styles.inner}>
        <div className={cx(styles.icon, styles.block)}></div>
        <div className={styles.details}>
          <div className={cx(styles.name, styles.block)}></div>
          <div className={styles.meta}>
            <span className={styles.block} /> | <span className={styles.block} />
          </div>
        </div>
      </div>
    </div>
  );
}

export { EntityBrowser, StreamsBrowser, CollectionsBrowser };